import { msg, type Message, type PlainKey } from '../i18n/t.ts';
import { copyFor } from './aidErrors.ts';
import { diffOps } from './wordDiff.ts';

/**
 * Say it back's logic, kept out of the `.tsx` so it runs under `node --test`
 * (say-it-back spec §7). Nothing here stores anything: an attempt lives in
 * component state and nowhere else (§4.7).
 */

/** What the worklet's decimator produces, and what the server's header says. */
export const SAMPLE_RATE = 16_000;
/** 0.3 s. Shorter is a mis-tap; refused here with copy, before any upload. */
export const MIN_SAMPLES = 4_800;
/** §4.3. The server allows 12 s, so a late timer is never a refusal. */
export const MAX_MS = 10_000;

export type Word = { text: string; differs: boolean };
export type Comparison = { target: Word[]; said: Word[]; same: boolean };

/**
 * Case and the punctuation STT adds are not differences: the transcriber
 * writes "I went home." for an exemplar stored as "I went home". Anything
 * else is, contractions included ("I'm" against "I am"). Equating those is
 * a linguistic call this mirror should not make silently (§4.5).
 */
function key(word: string): string {
  return word.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
}

/**
 * The target and the attempt, word by word, each flagged where it has no
 * partner on the other side. No score is derived from this and none should
 * be (§2): `same` exists only to choose one sentence of copy.
 *
 * A token with no letter or digit (a spaced "—", "…", a lone ".") is not a
 * word: it sits outside the diff and is never marked, so a dash the exemplar
 * has and speech cannot is not a difference. The target still shows it, as
 * written. An attempt made only of such tokens is empty, so it reads as
 * "I didn't catch anything" rather than as a result (§1, §4.6).
 */
export function compare(target: string, said: string): Comparison {
  const t = target.split(/\s+/).filter(Boolean);
  const s = said.split(/\s+/).filter(Boolean);
  const tWords = wordIndices(t);
  const sWords = wordIndices(s);
  const tDiff = t.map(() => false);
  const sDiff = s.map(() => false);
  for (const i of tWords) tDiff[i] = true;
  for (const j of sWords) sDiff[j] = true;
  const ops = diffOps(
    tWords.map((i) => key(t[i])),
    sWords.map((j) => key(s[j])),
    (x, y) => x === y,
  );
  for (const op of ops) {
    if (op.kind === 'same') {
      tDiff[tWords[op.i]] = false;
      sDiff[sWords[op.j]] = false;
    }
  }
  const saidWords = sWords.length === 0 ? [] : s.map((text, j) => ({ text, differs: sDiff[j] }));
  return {
    target: t.map((text, i) => ({ text, differs: tDiff[i] })),
    said: saidWords,
    same: sWords.length > 0 && !tDiff.some(Boolean) && !sDiff.some(Boolean),
  };
}

function wordIndices(tokens: readonly string[]): number[] {
  return tokens.flatMap((w, i) => (key(w) === '' ? [] : [i]));
}

export function joinPcm(chunks: readonly Int16Array[]): Int16Array {
  const out = new Int16Array(chunks.reduce((n, c) => n + c.length, 0));
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

/**
 * The samples as the wire wants them: PCM16 little-endian, base64. A
 * `DataView` rather than the array's own buffer, so the byte order is
 * written down rather than inherited from the platform.
 *
 * Chunked through `String.fromCharCode`, because spreading 320 000 bytes into
 * one call overflows the argument stack.
 */
export function pcmBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.length * 2);
  const view = new DataView(bytes.buffer);
  pcm.forEach((s, i) => view.setInt16(i * 2, s, true));
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

/**
 * `copyFor`'s `BAD_USER_INPUT` is about a native language and would be wrong
 * here. On this mutation it only ever means a recording outside the bounds,
 * which a learner fixes by holding the button properly.
 */
export function copyForAttempt(code: string | null): Message {
  return code === 'BAD_USER_INPUT' ? msg('say-back-hold') : copyFor(code);
}

export function micErrorCopy(e: unknown): Message {
  const name = (e as { name?: unknown } | null)?.name;
  return name === 'NotAllowedError' ? msg('say-back-mic-refused') : msg('say-back-mic-failed');
}

/** The button's phase as the card shows it; `Recorder` keeps finer ones to itself. */
export type ViewPhase = 'off' | 'starting' | 'ready' | 'recording' | 'sending';

/**
 * Every label the button can wear, so the view can stack them and size the
 * button to the widest. Measured at 119, 210, 145 and 151 px wide: a button
 * that resized with its label slid its right edge in and out under a thumb
 * mid-hold.
 */
export const LABELS: Record<ViewPhase, PlainKey> = {
  off: 'say-back-button',
  ready: 'say-back-button',
  starting: 'say-back-starting',
  recording: 'say-back-recording',
  sending: 'say-back-sending',
};

export const HINT = msg('say-back-hint');

/**
 * What the card shows under the button, as data. The rule it encodes is that
 * nothing a hold does may shrink the card: a result the learner has read is
 * `held` (kept in the layout, invisible, out of the accessibility tree) from
 * the next attempt's start until that attempt's own answer replaces it. §4.7's
 * "a new attempt replaces the last one" stays true of what is seen and heard,
 * without the ~164 px the result occupies vanishing at the press. That
 * collapse clamped the scroller whenever it sat within that distance of its
 * end, and re-centred a modal short enough to be centred; both moved the
 * button under the finger (BUILD.md, "Say it back holds still").
 *
 * `fresh` is whether `heard` answers the attempt now on screen.
 */
export function cardView(
  phase: ViewPhase,
  s: { heard: string | null; fresh: boolean; error: Message | null },
): {
  label: PlainKey;
  busy: boolean;
  status: { text: Message; alert: boolean } | null;
  result: 'none' | 'shown' | 'held';
} {
  const status = s.error
    ? { text: s.error, alert: true }
    : phase === 'ready' && s.heard === null
      ? { text: HINT, alert: false }
      : null;
  return {
    label: LABELS[phase],
    busy: phase === 'starting' || phase === 'sending',
    status,
    result: s.heard === null ? 'none' : s.fresh ? 'shown' : 'held',
  };
}

export type BridgeLike = { start(): Promise<void>; beginUtterance(): void; endUtterance(): void; stop(): void };

export type RecorderEvent =
  | { kind: 'starting' }
  | { kind: 'ready'; held: boolean }
  | { kind: 'recording' }
  | { kind: 'sending' }
  | { kind: 'clip'; pcm: Int16Array }
  | { kind: 'tooShort' }
  | { kind: 'micError'; error: unknown };

export type Timers = { set(fn: () => void, ms: number): unknown; clear(handle: unknown): void };

const realTimers: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

type Phase = 'off' | 'starting' | 'idle' | 'recording' | 'ending' | 'awaiting';

/**
 * The microphone for one card, as a state machine the tests can drive.
 *
 * The first press opens the microphone (§4.2), and records only if it is
 * still held once the microphone is open. A permission prompt takes the
 * pointer away mid-press, which arrives as a release while `starting`, so
 * that press only opens it and never starts an attempt nobody is holding;
 * with permission already granted, the learner's first hold is not thrown
 * away. After that, press and release bracket an attempt, and the worklet's
 * `ended`, not the release, closes it: a MessagePort delivers in order, so
 * `ended` follows every sample the hold produced (`capture.ts`'s `end`).
 *
 * A clip stays `awaiting` until the view calls `settle()` with its answer.
 * A press in between would record over an attempt the learner has not seen
 * back yet, and its answer would land mid-hold.
 */
export class Recorder {
  private phase: Phase = 'off';
  private bridge: BridgeLike | null = null;
  private chunks: Int16Array[] = [];
  private cap: unknown = null;
  private disposed = false;
  private held = false;

  // Assigned in the body, not as constructor parameter properties: node's
  // strip-only TS loader (`--test`, no `--experimental-transform-types`)
  // rejects that syntax outright, and this file must run under `node --test`
  // (say-it-back spec §7).
  private readonly make: (onPcm: (p: Int16Array) => void, onEnded: () => void) => BridgeLike;
  private readonly emit: (e: RecorderEvent) => void;
  private readonly timers: Timers;

  constructor(
    make: (onPcm: (p: Int16Array) => void, onEnded: () => void) => BridgeLike,
    emit: (e: RecorderEvent) => void,
    timers: Timers = realTimers,
  ) {
    this.make = make;
    this.emit = emit;
    this.timers = timers;
  }

  press(): void {
    if (this.disposed) return;
    if (this.phase === 'off') {
      this.phase = 'starting';
      this.held = true;
      const bridge = this.make(
        (p) => this.onPcm(p),
        () => this.onEnded(),
      );
      this.bridge = bridge;
      this.emit({ kind: 'starting' });
      bridge.start().then(
        () => {
          if (this.disposed) return bridge.stop();
          this.phase = 'idle';
          this.emit({ kind: 'ready', held: this.held });
          if (this.held) this.begin();
        },
        (error: unknown) => {
          bridge.stop();
          if (this.disposed) return;
          this.bridge = null;
          this.phase = 'off';
          this.emit({ kind: 'micError', error });
        },
      );
      return;
    }
    if (this.phase === 'idle') this.begin();
  }

  release(): void {
    this.held = false;
    if (this.disposed || this.phase !== 'recording') return;
    this.clearCap();
    this.phase = 'ending';
    this.bridge?.endUtterance();
    this.emit({ kind: 'sending' });
  }

  /** The clip's answer, or its failure, has reached the learner. */
  settle(): void {
    if (this.phase === 'awaiting') this.phase = 'idle';
  }

  private begin(): void {
    this.chunks = [];
    this.phase = 'recording';
    this.bridge?.beginUtterance();
    this.cap = this.timers.set(() => this.release(), MAX_MS);
    this.emit({ kind: 'recording' });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearCap();
    // While still opening, the `start` continuation stops it instead: stopping
    // a bridge whose `getUserMedia` has not resolved leaves the track it is
    // about to get running.
    if (this.phase !== 'starting') this.bridge?.stop();
    this.bridge = null;
  }

  private onPcm(p: Int16Array): void {
    if (this.phase === 'recording' || this.phase === 'ending') this.chunks.push(p);
  }

  private onEnded(): void {
    if (this.disposed || (this.phase !== 'ending' && this.phase !== 'recording')) return;
    this.clearCap();
    const pcm = joinPcm(this.chunks);
    this.chunks = [];
    if (pcm.length < MIN_SAMPLES) {
      this.phase = 'idle';
      this.emit({ kind: 'tooShort' });
    } else {
      this.phase = 'awaiting';
      this.emit({ kind: 'clip', pcm });
    }
  }

  private clearCap(): void {
    if (this.cap !== null) this.timers.clear(this.cap);
    this.cap = null;
  }
}

/**
 * Say it back's card, one `Recorder`, and so one microphone, per attempt
 * (media-channel design §4.5). Each is disposed the moment its attempt is
 * complete (a clip, too short, or refused) and before the clip is handed on.
 * An open capture stream puts a phone on the call channel, so a card holding
 * one would play its "Hear it" quieter after the first attempt than before
 * it. This is Say it with me's lifecycle (`sayWithMe.ts`).
 *
 * The same surface as `Recorder`, so the view swaps one for the other.
 * `settle()` keeps its meaning: no new attempt until the last one's answer
 * has reached the learner.
 */
export class AttemptRecorder {
  private current: Recorder | null = null;
  private awaiting = false;
  private disposed = false;
  // Assigned in the body, for `Recorder`'s reason.
  private readonly make: (onPcm: (p: Int16Array) => void, onEnded: () => void) => BridgeLike;
  private readonly emit: (e: RecorderEvent) => void;
  private readonly timers: Timers | undefined;

  constructor(
    make: (onPcm: (p: Int16Array) => void, onEnded: () => void) => BridgeLike,
    emit: (e: RecorderEvent) => void,
    timers?: Timers,
  ) {
    this.make = make;
    this.emit = emit;
    this.timers = timers;
  }

  press(): void {
    if (this.disposed || this.awaiting) return;
    if (!this.current) {
      const r = new Recorder(this.make, (e) => this.onEvent(r, e), this.timers);
      this.current = r;
    }
    this.current.press();
  }

  release(): void {
    this.current?.release();
  }

  settle(): void {
    this.awaiting = false;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.close();
  }

  private onEvent(r: Recorder, e: RecorderEvent): void {
    if (r !== this.current) return;
    if (e.kind === 'clip') {
      this.awaiting = true;
      this.close();
    } else if (e.kind === 'tooShort' || e.kind === 'micError') {
      this.close();
    } else if (e.kind === 'ready' && !e.held) {
      // Released before the microphone opened: a first permission prompt
      // takes the pointer. Nothing will be recorded, and an idle open stream
      // is the call channel this class exists to avoid (final review,
      // Important 2). The next press opens a fresh one.
      this.close();
    }
    this.emit(e);
  }

  private close(): void {
    this.current?.dispose();
    this.current = null;
  }
}
